<template>
    <DsFormField
        class="ds-datepicker"
        :class="[wrapperClass, { 'ds-datepicker--inline': inline }]"
        v-bind="wrapperProps"
    >
        <template #default="{ class: inputClasses }">
            <DatePicker
                ref="calendar"
                :name="id"
                :input-id="id"
                :disabled
                :invalid="!isValid"
                :selection-mode
                show-icon
                icon-display="input"
                breakpoint="1024px"
                date-format="d. m. yy"
                :base-z-index="baseZIndex ?? 990"
                aria-label="Kalendář pro výber data"
                :pt="getDatepickerPtOptions(inline ? [] : inputClasses)"
                @focus="() => emit('focus')"
                @show="() => (isCalendarOpened = true)"
                @hide="() => (isCalendarOpened = false)"
                v-model="modelValue"
                v-bind="attrs"
                unstyled
            >
                <template #inputicon="iconProps">
                    <DsButton
                        theme="light"
                        size="sm"
                        type="text"
                        icon-left="calendar"
                        class="ds-datepicker__input-icon"
                        aria-hidden="true"
                        tabindex="-1"
                        @click="iconProps.clickCallback"
                        :is-in-storybook
                    ></DsButton>
                </template>
                <template #previcon="prevIconProps"
                    ><DsIcon name="chevron-left" v-bind="prevIconProps" :is-in-storybook
                /></template>
                <template #nexticon="nextIconProps"
                    ><DsIcon name="chevron-right" v-bind="nextIconProps" :is-in-storybook
                /></template>

                <template #incrementicon="incProps">
                    <DsIcon name="chevron-up" v-bind="incProps" :is-in-storybook />
                </template>
                <template #decrementicon="decProps">
                    <DsIcon name="chevron-down" v-bind="decProps" :is-in-storybook />
                </template>

                <template #date="dateProps" v-if="hightlightedDatesFnc">
                    <span class="ds-datepicker__day-value"> {{ dateProps.date.day }} </span>
                    <span v-if="hightlightedDatesFnc(dateProps.date)" class="ds-datepicker__day-highlight"></span>
                </template>
                <template #footer v-if="selectionMode === 'range'">
                    <div class="ds-datepicker__footer">
                        <DsButton type="interaction" size="sm" @click="clearValue" :is-in-storybook
                            >Zrušit filtr</DsButton
                        >
                        <DsButton type="interaction" size="sm" @click="isCalendarOpened = false" :is-in-storybook
                            >Zavřít</DsButton
                        >
                    </div>
                </template>
            </DatePicker>
        </template>
        <template #inputRight></template>
    </DsFormField>
</template>

<script lang="ts" setup generic="T extends Date | Date[]">
import { computed, onMounted, ref, useAttrs, watchEffect } from "vue";
import DatePicker, { type DatePickerDateSlotOptions, type DatePickerState } from "primevue/datepicker";
import DsIcon from "../../icon/ds-icon.vue";
import { getDatepickerPtOptions } from "./ds-datepicker/ds-datepicker-pt";
import { DsFormFieldProps } from "./general/ds-form-field.vue";
import DsFormField, { stripFormFieldProps } from "./general/ds-form-field.vue";
import DsButton from "../../button/ds-button.vue";

type SelectedModeType = "single" | "multiple" | "range";

const emit = defineEmits(["focus"]);

defineOptions({ inheritAttrs: false });
const { class: wrapperClass, ...attrs } = useAttrs();

/**
 *  Selected date(s) in the calendar. If selectionMode is 'range', it will hold an array of dates.
 */
const modelValue = defineModel<T>();

/**
 * Indicates if the calendar is opened.
 */
const isCalendarOpened = defineModel<boolean>("isCalendarOpened", { required: false, default: false });

const props = defineProps<
    DsFormFieldProps & {
        /**
         * Defines the quantity of the selection.
         * Sigle: classic single-date selection.
         * Multiple: allows multiple dates to be selected. Selected dates will be represented as an array.
         * Range: allows a range of dates to be selected. First and last selected dates will be represented as an array.
         */
        selectionMode?: SelectedModeType;

        /**
         * Defines the date(s) to be highlighted.
         */
        hightlightedDatesFnc?: (date: DatePickerDateSlotOptions) => boolean;

        /**
         * If true, the datepicker will be displayed over other element.
         */
        inline?: boolean;

        /**
         * Z-index of the datepicker overlay. If not set, it will use the default 990.
         */
        baseZIndex?: number;
    }
>();

const wrapperProps = computed(() => stripFormFieldProps(props));

const isValid = computed(() => !props.errorMessage);

const calendar = ref<InstanceType<typeof DatePicker> & DatePickerState>(null);

onMounted(() => {
    watchEffect(() => {
        calendar.value.overlayVisible = isCalendarOpened.value;
    });
});

const clearValue = () => {
    modelValue.value = null;
    isCalendarOpened.value = false;
};
</script>
